import { Component, Input, Output, EventEmitter, AfterViewInit, NgZone, OnInit, inject } from '@angular/core';
import { ScriptLoaderService } from '../../../services/script-loader/script-loader.service';
import { OrganizationService, RnCommonId } from '../../../services/rnapi2-service';
import { firstValueFrom, take } from 'rxjs';


declare global {
  interface Window {
    Zuora?: any;
  }
}

export interface PaymentSuccess {
  paymentId: string;
  paymentMethodId: string;
}

export interface PaymentError {
  code?: string;
  message: string;
  raw?: any;
}

@Component({
  selector: 'app-payment-form',
  templateUrl: './payment-form.component.html',
  styleUrl: './payment-form.component.scss',
  standalone: false
})
export class PaymentFormComponent implements OnInit, AfterViewInit {
  private readonly scripts = inject(ScriptLoaderService);
  private readonly orgService = inject(OrganizationService);
  private readonly zone = inject(NgZone);

  @Input() scriptUrl = 'https://js.zuora.com/payment/1.4.0/zuora.js';
  @Input() containerId = 'zuora-payment-form';
  @Input() theme?: any;            // optional styling config
  @Input() additionalConfig?: any; // gateway, allowed methods, etc.
  @Input() paymentFormNumber: string;
  @Input() isUpdate: boolean;
  @Input() zuoraId: string;
  @Input() firstName: string;
  @Input() lastName: string;
  @Input() organizationId: string;
  @Input() allowAch: boolean;

  @Output() paymentInit = new EventEmitter<void>();
  @Output() paymentSuccess = new EventEmitter<PaymentSuccess>();
  @Output() paymentError = new EventEmitter<PaymentError>();
  @Output() formReady = new EventEmitter<void>();

  loading = true;
  private initialized = false;
  paymentSessionToken: string;
  amount: string;
  apiEndPoint: string;
  publishableKey: string;

  ngOnInit() {
    this.amount = '1.00';
  }

  ngAfterViewInit():void {
    void this.initAfterViewAsync();
  }

  initAfterViewAsync = async ():Promise<void> => {
    try {
      await this.scripts.load(this.scriptUrl);
      this.publishableKey = await this.fetchConfigurableValue('zuora_paymentforms_publishable_key');
      this.paymentFormNumber = this.allowAch ? await this.fetchConfigurableValue('zuora_paymentforms_form_apphub') : await this.fetchConfigurableValue('zuora_paymentforms_apphub_cconly');
      this.initZuoraForm();
    } catch (e: any) {
      this.zone.run(() =>
        this.paymentError.emit({ message: e?.message ?? 'Failed to load Zuora script', raw: e })
      );
      this.loading = false;
    }
  }

  private async fetchConfigurableValue(name: string): Promise<string> {
    try {
      const response = await firstValueFrom(
        this.orgService.apiV2OrganizationsGetconfigurablevaluePost({
          search: name
        })
      );
      const key = typeof response === 'string' ? response : response?.data;
      if (!key) {
        throw new Error('Not Found');
      }
      return key;
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : 'Configuration error occurred'
      );
    }
  }

  private initZuoraForm() {
    const zuora = globalThis.Zuora(this.publishableKey);

    const config = {
      locale: "en",
      region: "US",
      currency: "USD",
      amount: this.amount,
      profile: this.paymentFormNumber,
      createPaymentSession: (paymentSessionContext) => {
        // generate payment session when end-customer click on the Pay button.
        return new Promise((resolve, reject) => {
          const rnCommonIdPayload = new RnCommonId();
          rnCommonIdPayload.Id = this.organizationId;
          this.paymentInit.emit();
          this.orgService.apiV2OrganizationsPaymentSessionPost(rnCommonIdPayload)
            .pipe(take(1))
            .subscribe({
              next: (paymentSessionTokenResponse: any) => {
                const token = paymentSessionTokenResponse.data;
                this.paymentSessionToken = token;
                resolve(token);
              },
              error: (err) => {
                console.error("Error occurred while creating payment session");
                console.error(err);
                if (err instanceof Error) {
                  reject(err);
                } else if (err && typeof err === 'object' && 'message' in err) {
                  const msg = String((err as { message?: unknown }).message ?? 'Unknown error');
                  const wrapped = new Error(msg);
                  (wrapped as any).cause = err;
                  reject(wrapped);
                } else if (typeof err === 'string') {
                  reject(new Error(err));
                } else {
                  reject(new Error('Unknown error while creating payment session'));
                }


              }
            });
        });
      },
      onComplete: (result) => {
        console.log("==========");
        console.log("Payment Result");
        console.log("==========");
        console.log(`transaction result: ${JSON.stringify(result)}`);
        if (result.success) {
          const paymentSuccess = {
            paymentId: result.paymentId,
            paymentMethodId: result.paymentMethodId
          };
          this.paymentSuccess.emit(paymentSuccess);

        } else {
          this.paymentError.emit({ message: 'Failed saving payment info' });
        }
      }
    };

    zuora.createPaymentForm(config).then((form) => {
      form.mount("#" + this.containerId)
      this.loading = false;
    }).catch((error) => {
      console.error("Error occurred while creating payment form.")
      console.error(error);
    });
  }
}
